import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Inbox, RefreshCw, Loader2, Link2, X, Mail, MailOpen, Paperclip,
  ChevronDown, ChevronUp, Server, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const InboxTab = ({ password }) => {
  const [mails, setMails] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [imapStatus, setImapStatus] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');
  const [linkingId, setLinkingId] = useState(null);
  const [linkSearch, setLinkSearch] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const headers = useMemo(() => ({ 'x-admin-password': password }), [password]);

  const fetchAll = useCallback(async () => {
    try {
      const [mRes, lRes, sRes] = await Promise.all([
        fetch(`${API}/admin/inbox/unmatched?limit=200`, { headers }),
        fetch(`${API}/admin/outreach/targets`, { headers }),
        fetch(`${API}/admin/imap/status`, { headers }),
      ]);
      if (mRes.ok) setMails(await mRes.json());
      if (lRes.ok) {
        const d = await lRes.json();
        setLeads(Array.isArray(d) ? d : (d.targets || []));
      }
      if (sRes.ok) setImapStatus(await sRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const triggerSync = async () => {
    setSyncing(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/admin/imap/sync`, { method: 'POST', headers });
      const data = await res.json();
      if (!data.ok && data.reason === 'not_configured') {
        alert('IMAP nicht konfiguriert. Bitte tragen Sie IMAP_HOST, IMAP_USERNAME und IMAP_PASSWORD in der Backend-.env ein und starten Sie den Backend-Service neu.');
      } else {
        const total = (data.folders || []).reduce((acc, f) => acc + (f.fetched || 0), 0);
        const matched = (data.folders || []).reduce((acc, f) => acc + (f.matched || 0), 0);
        const unmatched = (data.folders || []).reduce((acc, f) => acc + (f.unmatched || 0), 0);
        const errors = (data.folders || []).filter((f) => f.error).map((f) => `${f.folder}: ${f.error}`);
        let msg = `${total} neue Mail(s) abgerufen — ${matched} zugeordnet, ${unmatched} unzugeordnet.`;
        if (errors.length) msg += `\n\nFehler:\n${errors.join('\n')}`;
        alert(msg);
      }
      fetchAll();
    } catch (err) {
      alert(`Sync fehlgeschlagen: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/admin/imap/test`, { method: 'POST', headers });
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const linkMail = async (mailId, leadId) => {
    try {
      const res = await fetch(`${API}/admin/inbox/unmatched/${mailId}/link`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: leadId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setLinkingId(null);
      setLinkSearch('');
      fetchAll();
    } catch (err) {
      alert(`Verknüpfen fehlgeschlagen: ${err.message}`);
    }
  };

  const dismissMail = async (mailId) => {
    if (!window.confirm('Diese Mail aus der Inbox entfernen?')) return;
    try {
      const res = await fetch(`${API}/admin/inbox/unmatched/${mailId}`, {
        method: 'DELETE', headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchAll();
    } catch (err) {
      alert(`Entfernen fehlgeschlagen: ${err.message}`);
    }
  };

  const filteredMails = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mails;
    return mails.filter((m) => {
      const blob = [
        m.subject, m.from_addr, (m.to_addrs || []).join(' '), m.body,
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [mails, search]);

  const filteredLeads = useMemo(() => {
    const q = linkSearch.trim().toLowerCase();
    if (!q) return leads.slice(0, 20);
    return leads.filter((l) => {
      const blob = [l.name, l.city, l.email1, l.email2, l.contact_person].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    }).slice(0, 20);
  }, [leads, linkSearch]);

  return (
    <div className="space-y-6" data-testid="inbox-tab">
      {/* Header / Status */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Inbox size={22} className="text-orange-500" />
            Inbox — Unzugeordnete E-Mails
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Mails aus dem IMAP-Postfach, deren Absender/Empfänger keinem Lead zugeordnet werden konnte.
            Verknüpfen Sie sie manuell oder verwerfen Sie sie.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={testConnection}
            disabled={testing}
            data-testid="imap-test-btn"
          >
            {testing ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Server size={13} className="mr-1.5" />}
            IMAP-Verbindung testen
          </Button>
          <Button
            size="sm"
            onClick={triggerSync}
            disabled={syncing}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="imap-sync-btn"
          >
            {syncing ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <RefreshCw size={13} className="mr-1.5" />}
            Jetzt synchronisieren
          </Button>
        </div>
      </div>

      {/* IMAP status banner */}
      {imapStatus && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            imapStatus.configured
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
          data-testid="imap-status-banner"
        >
          {imapStatus.configured ? (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={15} />
                <span><strong>IMAP aktiv:</strong> {imapStatus.username} @ {imapStatus.host}:{imapStatus.port} ({imapStatus.folder}{imapStatus.sent_folder ? ` + ${imapStatus.sent_folder}` : ''})</span>
              </div>
              {imapStatus.state?.last_sync_at && (
                <span className="text-xs text-emerald-700">
                  Letzter Sync: {formatDate(imapStatus.state.last_sync_at)}
                  {imapStatus.state.last_error ? ` · Fehler: ${imapStatus.state.last_error}` : ''}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <AlertCircle size={15} className="mt-0.5" />
              <div>
                <strong>IMAP nicht konfiguriert.</strong> Tragen Sie in der Backend-<code>.env</code> ein:
                <pre className="mt-1 text-xs bg-amber-100/60 p-2 rounded font-mono">
{`IMAP_HOST=imap.ionos.de       # bei IONOS; All-Inkl: w0XXXXX.kasserver.com
IMAP_PORT=993
IMAP_USERNAME=ihre@adresse.de
IMAP_PASSWORD=••••••••••••
IMAP_FOLDER=INBOX
IMAP_SENT_FOLDER=Sent          # optional, für ausgehende Mails`}
                </pre>
                <span className="text-xs">Anschließend Backend neu starten — Polling läuft dann alle 10 Min. automatisch.</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Test result */}
      {testResult && (
        <div
          className={`rounded-md border p-2.5 text-sm ${
            testResult.ok
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {testResult.ok
            ? `✓ Verbindung OK — Ordner '${testResult.folder}' verfügbar.`
            : `✗ ${testResult.error}`}
        </div>
      )}

      {/* Search */}
      {mails.length > 0 && (
        <Input
          placeholder="Mails durchsuchen (Betreff, Absender, Inhalt) …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
          data-testid="inbox-search"
        />
      )}

      {/* Mail list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-slate-400" size={28} />
        </div>
      ) : filteredMails.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-lg p-12 text-center text-slate-400" data-testid="inbox-empty">
          {mails.length === 0
            ? 'Keine unzugeordneten Mails — entweder ist alles zugeordnet, oder das IMAP-Polling hat noch keine Mails geholt.'
            : 'Keine Mails passen zum Suchbegriff.'}
        </div>
      ) : (
        <div className="space-y-2" data-testid="inbox-list">
          {filteredMails.map((m) => {
            const expanded = expandedId === m.id;
            const isLinking = linkingId === m.id;
            const direction = m.direction_hint || 'email_in';
            const Icon = direction === 'email_out' ? Mail : MailOpen;
            return (
              <div
                key={m.id}
                className="border border-slate-200 rounded-lg bg-white"
                data-testid={`inbox-item-${m.id}`}
              >
                <div className="flex items-start gap-3 p-3">
                  <div className="shrink-0 w-9 h-9 rounded-md bg-sky-50 border border-sky-200 flex items-center justify-center">
                    <Icon size={15} className="text-sky-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                        {direction === 'email_out' ? 'Gesendet' : 'Eingegangen'}
                      </span>
                      <span className="text-xs text-slate-400">{formatDate(m.message_date)}</span>
                      {m.attachments?.length > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-slate-500">
                          <Paperclip size={11} /> {m.attachments.length}
                        </span>
                      )}
                    </div>
                    <div className="font-medium text-sm text-slate-900 mt-0.5 truncate">
                      {m.subject || '(kein Betreff)'}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      Von: {m.from_addr || '—'}
                      {m.to_addrs?.length > 0 && <> · An: {m.to_addrs.slice(0, 2).join(', ')}{m.to_addrs.length > 2 ? '…' : ''}</>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setLinkingId(isLinking ? null : m.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100"
                      data-testid={`inbox-link-${m.id}`}
                    >
                      <Link2 size={12} /> Lead zuordnen
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : m.id)}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                      title={expanded ? 'Einklappen' : 'Vorschau'}
                      data-testid={`inbox-toggle-${m.id}`}
                    >
                      {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => dismissMail(m.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                      title="Verwerfen"
                      data-testid={`inbox-dismiss-${m.id}`}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>

                {isLinking && (
                  <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`inbox-linkbox-${m.id}`}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Lead auswählen
                    </div>
                    <Input
                      placeholder="Nach Name, Stadt, E-Mail suchen …"
                      value={linkSearch}
                      onChange={(e) => setLinkSearch(e.target.value)}
                      className="bg-white"
                      autoFocus
                    />
                    <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-md bg-white divide-y divide-slate-100">
                      {filteredLeads.length === 0 ? (
                        <div className="p-3 text-sm text-slate-400 italic text-center">
                          Keine passenden Leads.
                        </div>
                      ) : filteredLeads.map((l) => (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => linkMail(m.id, l.id)}
                          className="w-full text-left px-3 py-2 hover:bg-orange-50 transition-colors"
                        >
                          <div className="font-medium text-sm text-slate-900">{l.name}</div>
                          <div className="text-xs text-slate-500">
                            {[l.city, l.email1, l.email2].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {expanded && (
                  <div className="border-t border-slate-100 bg-slate-50 px-3 py-3 text-sm text-slate-700 whitespace-pre-wrap" data-testid={`inbox-body-${m.id}`}>
                    {m.body || '(kein Inhalt)'}
                    {m.attachments?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                          Anhänge ({m.attachments.length})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {m.attachments.map((a, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-slate-300 bg-white text-xs text-slate-700"
                            >
                              <Paperclip size={11} />
                              {a.filename}
                            </span>
                          ))}
                        </div>
                        <div className="text-xs text-slate-400 mt-2 italic">
                          Anhänge werden nach Verknüpfung beim Lead heruntergeladen werden können.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InboxTab;
